/**
 * @file useDocumentCard.ts
 * @description Данные карточки документа (phase19.31, часть 3; вынесено из
 * страницы): документ, загрузка, ошибка человеческим текстом, перечитывание.
 *
 * Перечитывание тихое: документ остаётся на экране, пока идёт запрос. После
 * действия (выпуск, подпись, аннулирование) сервер возвращает обновлённый
 * документ — его кладём через `replace`, лишнего запроса нет.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError } from "../../../lib/api-client";
import { getDocument, type DocumentDto } from "../../../lib/api-crm";

export interface DocumentCardState {
  doc: DocumentDto | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  /** Положить документ, который вернул сервер после действия. */
  replace: (doc: DocumentDto) => void;
}

export function useDocumentCard(id: string | undefined): DocumentCardState {
  const [doc, setDoc] = useState<DocumentDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const requestId = useRef(0);

  useEffect(() => {
    if (!id) return;
    const current = ++requestId.current;
    setLoading(true);
    getDocument(id)
      .then((res) => {
        if (current !== requestId.current) return;
        setDoc(res);
        setError(null);
      })
      .catch((err: unknown) => {
        if (current !== requestId.current) return;
        setError(err instanceof ApiError && err.status === 404 ? "Документ не найден" : "Не удалось загрузить документ. Проверьте связь и попробуйте ещё раз");
      })
      .finally(() => {
        if (current === requestId.current) setLoading(false);
      });
  }, [id, attempt]);

  const refresh = useCallback(() => setAttempt((n) => n + 1), []);
  const replace = useCallback((next: DocumentDto) => setDoc(next), []);

  return { doc, loading, error, refresh, replace };
}
